import { Box, TableCell, TableRow } from '@mui/material'
import TimepointSubmissionChip from './TimepointSubmissionChip.tsx'
import AddTimepointSubmissionChip from './AddTimepointSubmissionChip.tsx'
import { TimepointSubmissionRow } from '@features/subject/subject.types.ts'
import { useNavigate, useParams, createSearchParams } from 'react-router-dom'
import { generatePathWithParams } from '@common/utils/urlUtils.ts'
import { ROUTES } from 'src/routes'

export default function TimepointSubmissionTableRow({
  timepointSubmissionRow,
  subjectId,
  limit,
  previous_cursor,
  next_cursor,
}: {
  timepointSubmissionRow: TimepointSubmissionRow
  subjectId: string
  limit?: number
  previous_cursor?: string | null | undefined
  next_cursor?: string | null | undefined
}) {
  const navigate = useNavigate()
  const { studyId } = useParams<{
    studyId: string
  }>()

  const navigateToSubjectDetails = (timepointSubmissionId: string | undefined) => {
    const searchParams = {
      limit: (limit || 50).toString(),
      previous_cursor: previous_cursor || '',
      next_cursor: next_cursor || '',
    }
    const path = generatePathWithParams(ROUTES.SUBJECT_TIMEPOINT_SUBMISSION.path, {
      studyId: studyId!,
      subjectId: subjectId!,
      timepointSubmissionId: timepointSubmissionId ?? null,
    })

    navigate({ pathname: path, search: `?${createSearchParams(searchParams)}` })
  }

  return (
    <TableRow
      data-testid="timepoint-submission-row"
      sx={{
        zIndex: 1,
        position: 'relative',
      }}
    >
      <TableCell
        padding="checkbox"
        sx={{
          width: '20%',
          minWidth: 48,
          bgcolor: 'background.default',
          position: 'sticky',
          left: 0,
          zIndex: 30,
          boxSizing: 'border-box',
          tableLayout: 'fixed',
        }}
        data-testid="empty-cell-0"
      />
      <TableCell
        sx={{
          width: 170, // Subject ID - matches DefaultSubjectTableHeader
          minWidth: 170,
          bgcolor: 'background.default',
          position: 'sticky',
          left: 48,
          zIndex: 30,
          boxSizing: 'border-box',
          tableLayout: 'fixed',
        }}
        data-testid="empty-cell-1"
      />
      <TableCell
        sx={{
          width: 120, // Blind Code - reduced from 170
          minWidth: 120,
          bgcolor: 'background.default',
          position: 'sticky',
          left: 168, // 48 + 120
          zIndex: 30,
          boxSizing: 'border-box',
          tableLayout: 'fixed',
        }}
        data-testid="empty-cell-2"
      />
      <TableCell
        sx={{
          width: 120, // Site ID - kept same
          minWidth: 120,
          bgcolor: 'background.default',
          position: 'sticky',
          left: 288, // 48 + 120 + 120
          zIndex: 30,
          boxSizing: 'border-box',
          tableLayout: 'fixed',
        }}
        data-testid="empty-cell-3"
      />
      <TableCell
        sx={{
          width: 120, // Status - reduced from 170
          minWidth: 120,
          bgcolor: 'background.default',
          position: 'sticky',
          left: 408, // 48 + 120 + 120 + 170
          zIndex: 30,
          boxSizing: 'border-box',
          tableLayout: 'fixed',
        }}
        data-testid="empty-cell-4"
      />
      <TableCell
        sx={{
          width: 120, // Query - reduced from 170
          minWidth: 120,
          bgcolor: 'background.default',
          position: 'sticky',
          left: 528, // 48 + 120 + 120 + 170 + 120
          zIndex: 30,
          boxSizing: 'border-box',
          tableLayout: 'fixed',
        }}
        data-testid="empty-cell-5"
      />
      {timepointSubmissionRow.timepoint_submissions.map((timepointSubmission, index) => (
        <TableCell
          key={index}
          sx={{
            paddingX: 0.5,
            paddingY: 0.25,
            minWidth: 220,
            boxSizing: 'border-box',
            zIndex: 1,
            position: 'relative',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
          data-testid={`timepoint-cell-${index}`}
        >
          {timepointSubmission.timepoint_submission ? (
            <Box
              data-testid={`timepoint-box-${index}`}
              sx={{
                cursor: 'pointer',
                zIndex: 1,
                position: 'relative',
              }}
              onClick={() =>
                navigateToSubjectDetails(timepointSubmission.timepoint_submission?.timepoint_submission_id)
              }
            >
              <TimepointSubmissionChip
                timepoint={timepointSubmission.timepoint_submission}
                data-testid={`timepoint-chip-${index}`}
              />
            </Box>
          ) : (
            timepointSubmission.isLast && (
              <AddTimepointSubmissionChip
                timepointId={timepointSubmission.timepoint_id || ''}
                subjectId={subjectId}
                data-testid={`add-timepoint-chip-${index}`}
              />
            )
          )}
        </TableCell>
      ))}
    </TableRow>
  )
}
